'use client';

/**
 * Team Management Page
 * Manage workspace members and invitations
 */

import { useState, useEffect, useCallback } from 'react';
import { WorkspaceProvider, useWorkspace } from '@/lib/archimate/WorkspaceContext';
import { CommandCenterProvider, useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import CommandCenterHeader from '@/components/command-center/CommandCenterHeader';

interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'pending' | 'suspended';
  invited_by: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  created_at: string;
}

interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  workspace_name: string | null;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  token: string;
  message: string | null;
  invited_by: string;
  invited_by_name: string | null;
  expires_at: string;
  email_sent_at: string | null;
  email_sent_count: number;
  inviteLink: string;
  created_at: string;
}

interface TeamStats {
  total: number;
  active: number;
  pending: number;
  byRole: {
    owner: number;
    admin: number;
    member: number;
    viewer: number;
  };
}

const ROLE_CONFIG: Record<string, { label: string; description: string; color: string; bgColor: string }> = {
  owner: { label: 'Owner', description: 'Full control', color: 'text-accent', bgColor: 'bg-accent-muted' },
  admin: { label: 'Admin', description: 'Manage members', color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  member: { label: 'Member', description: 'Create & edit', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  viewer: { label: 'Viewer', description: 'Read-only', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function TeamContent() {
  const { currentWorkspace, loading: workspaceLoading } = useWorkspace();
  const { setWorkspaceId } = useCommandCenter();

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member' as 'admin' | 'member' | 'viewer',
    message: '',
    send_email: true,
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  useEffect(() => {
    if (currentWorkspace?.id) {
      setWorkspaceId(currentWorkspace.id);
    }
  }, [currentWorkspace?.id, setWorkspaceId]);

  const loadTeam = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/secure/command-center/workspace-members?workspace_id=${currentWorkspace.id}`);
      if (!response.ok) {
        if (response.status === 403) {
          setError('You do not have access to this workspace');
          return;
        }
        throw new Error('Failed to load team');
      }

      const data = await response.json();
      setMembers(data.members || []);
      setInvitations(data.invitations || []);
      setStats(data.stats || null);
      setCurrentUserRole(data.currentUserRole);
      setCanManageMembers(data.canManageMembers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace?.id) return;

    setInviteLoading(true);
    setInviteError(null);
    setLastInviteLink(null);

    try {
      const response = await fetch('/api/secure/command-center/workspace-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: currentWorkspace.id,
          workspace_name: currentWorkspace.name,
          email: inviteForm.email,
          role: inviteForm.role,
          message: inviteForm.message || undefined,
          send_email: inviteForm.send_email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      setLastInviteLink(data.inviteLink);
      setInviteForm({ email: '', role: 'member', message: '', send_email: true });
      loadTeam();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = async (link: string, id: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(id);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch {
      console.error('Failed to copy link');
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    try {
      const response = await fetch(`/api/secure/command-center/workspace-invitations/resend/${invitationId}`, {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to resend invitation');
      }

      loadTeam();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resend invitation');
    }
  };

  const handleCancelInvite = async (invitationId: string) => {
    if (!confirm('Cancel this invitation?')) return;

    try {
      const response = await fetch(`/api/secure/command-center/workspace-invitations/${invitationId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to cancel invitation');
      loadTeam();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    try {
      const response = await fetch(`/api/secure/command-center/workspace-members/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update role');
      }

      loadTeam();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from this workspace?`)) return;

    try {
      const response = await fetch(`/api/secure/command-center/workspace-members/${memberId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove member');
      }

      loadTeam();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  if (workspaceLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <CommandCenterHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-accent"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-background">
        <CommandCenterHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-foreground-tertiary">
            <p>Please select a workspace to manage team members.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CommandCenterHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Team</h1>
            <p className="text-foreground-tertiary mt-1">
              Manage members of <span className="text-accent">{currentWorkspace.name}</span>
            </p>
          </div>
          <div className="flex items-center gap-4">
            {currentUserRole && (
              <span className={`px-3 py-1 rounded-full text-sm ${ROLE_CONFIG[currentUserRole]?.bgColor} ${ROLE_CONFIG[currentUserRole]?.color}`}>
                You: {ROLE_CONFIG[currentUserRole]?.label}
              </span>
            )}
            {canManageMembers && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent-hover transition-colors"
              >
                Invite Member
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-surface border border-border-muted rounded-lg p-4">
              <div className="text-2xl font-bold text-foreground">{stats.total}</div>
              <div className="text-sm text-foreground-tertiary">Total Members</div>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-4">
              <div className="text-2xl font-bold text-accent">{stats.byRole.owner}</div>
              <div className="text-sm text-foreground-tertiary">Owners</div>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-400">{stats.byRole.admin}</div>
              <div className="text-sm text-foreground-tertiary">Admins</div>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.byRole.member}</div>
              <div className="text-sm text-foreground-tertiary">Members</div>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-400">{stats.pending}</div>
              <div className="text-sm text-foreground-tertiary">Pending</div>
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="bg-surface border border-border-muted rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-border-muted">
            <h2 className="text-lg font-semibold text-foreground">Members</h2>
          </div>
          <div className="divide-y divide-border-muted">
            {members.length === 0 ? (
              <div className="p-6 text-center text-foreground-tertiary">No members yet</div>
            ) : (
              members.map((member) => (
                <div key={member.id} className="p-4 flex items-center justify-between hover:bg-cream/5">
                  <div className="flex items-center gap-4">
                    {member.avatar_url ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img src={member.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-cream/20 flex items-center justify-center text-foreground font-medium">
                        {(member.name || member.email)[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-foreground font-medium">
                        {member.name || member.email}
                      </div>
                      <div className="text-sm text-foreground-tertiary">{member.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {canManageMembers && member.role !== 'owner' ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                        className="bg-background border border-border text-foreground text-sm rounded px-2 py-1"
                      >
                        {currentUserRole === 'owner' && <option value="admin">Admin</option>}
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-sm ${ROLE_CONFIG[member.role]?.bgColor} ${ROLE_CONFIG[member.role]?.color}`}>
                        {ROLE_CONFIG[member.role]?.label}
                      </span>
                    )}
                    {canManageMembers && member.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveMember(member.id, member.name || member.email)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Invitations */}
        {canManageMembers && invitations.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-border-muted">
              <h2 className="text-lg font-semibold text-foreground">Pending Invitations</h2>
            </div>
            <div className="divide-y divide-border-muted">
              {invitations.map((invite) => (
                <div key={invite.id} className="p-4 flex items-center justify-between hover:bg-cream/5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400">
                      ✉️
                    </div>
                    <div>
                      <div className="text-foreground">{invite.email}</div>
                      <div className="text-sm text-foreground-tertiary">
                        Invited {formatDate(invite.created_at)} · Expires {formatDate(invite.expires_at)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-sm ${ROLE_CONFIG[invite.role]?.bgColor} ${ROLE_CONFIG[invite.role]?.color}`}>
                      {ROLE_CONFIG[invite.role]?.label}
                    </span>
                    <button
                      onClick={() => handleCopyLink(invite.inviteLink, invite.id)}
                      className="text-foreground-tertiary hover:text-foreground text-sm"
                    >
                      {copiedLink === invite.id ? '✓ Copied' : 'Copy Link'}
                    </button>
                    <button
                      onClick={() => handleResendInvite(invite.id)}
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowInviteModal(false)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
              <div className="bg-surface border border-border rounded-lg max-w-md w-full p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Invite Team Member</h2>

                <form onSubmit={handleInvite}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-1">Email</label>
                      <input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        className="w-full bg-background border border-border text-foreground rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring/50"
                        placeholder="colleague@company.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-1">Role</label>
                      <select
                        value={inviteForm.role}
                        onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as 'admin' | 'member' | 'viewer' })}
                        className="w-full bg-background border border-border text-foreground rounded-lg px-3 py-2"
                      >
                        {currentUserRole === 'owner' && <option value="admin">Admin - Can manage members</option>}
                        <option value="member">Member - Can create & edit</option>
                        <option value="viewer">Viewer - Read-only access</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-1">Message (optional)</label>
                      <textarea
                        value={inviteForm.message}
                        onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                        className="w-full bg-background border border-border text-foreground rounded-lg px-3 py-2 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-ring/50"
                        placeholder="Add a personal note..."
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="send_email"
                        checked={inviteForm.send_email}
                        onChange={(e) => setInviteForm({ ...inviteForm, send_email: e.target.checked })}
                        className="rounded border-border"
                      />
                      <label htmlFor="send_email" className="text-sm text-foreground-secondary">
                        Send email invitation
                      </label>
                    </div>

                    {inviteError && (
                      <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
                        {inviteError}
                      </div>
                    )}

                    {lastInviteLink && (
                      <div className="p-3 bg-green-500/20 border border-green-500/50 rounded">
                        <div className="text-green-400 text-sm mb-2">✓ Invitation created!</div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={lastInviteLink}
                            className="flex-1 bg-background border border-border text-foreground text-xs rounded px-2 py-1"
                          />
                          <button
                            type="button"
                            onClick={() => handleCopyLink(lastInviteLink, 'new')}
                            className="text-sm text-accent hover:text-accent"
                          >
                            {copiedLink === 'new' ? '✓' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteError(null);
                        setLastInviteLink(null);
                      }}
                      className="px-4 py-2 text-foreground-secondary hover:text-foreground"
                    >
                      {lastInviteLink ? 'Done' : 'Cancel'}
                    </button>
                    {!lastInviteLink && (
                      <button
                        type="submit"
                        disabled={inviteLoading}
                        className="px-4 py-2 bg-accent text-accent-foreground font-medium rounded-lg hover:bg-accent-hover disabled:opacity-50"
                      >
                        {inviteLoading ? 'Sending...' : 'Send Invitation'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function TeamPage() {
  return (
    <WorkspaceProvider>
      <CommandCenterProvider>
        <TeamContent />
      </CommandCenterProvider>
    </WorkspaceProvider>
  );
}
